// src/components/layout/Navbar.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import '../../styles/navbar.css';

const Navbar = () => {
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  
  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle search input changes
  useEffect(() => {
    const searchTimer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch();
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 500);
    
    return () => clearTimeout(searchTimer);
  }, [searchQuery]);
  
  const performSearch = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/api/users/search', {
        params: { q: searchQuery }
      });
      
      setSearchResults(response.data.results);
      setShowResults(true);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const handleUserClick = (username) => {
    setSearchQuery('');
    setShowResults(false);
    navigate(`/profile/${username}`);
  };
  
  const handleFollowToggle = async (username, isCurrentlyFollowing, e) => {
    e.stopPropagation(); // Prevent navigation
    
    try {
      if (isCurrentlyFollowing) {
        await api.delete(`/api/follows/${username}`);
      } else {
        await api.post(`/api/follows/${username}`);
      }
      
      // Update the follow status in results
      setSearchResults(searchResults.map(user => {
        if (user.username === username) {
          return { ...user, isFollowing: !isCurrentlyFollowing };
        }
        return user;
      }));
    } catch (err) {
      console.error('Follow toggle error:', err);
    }
  };

  const getIMGURL = (url) => {
    if (!url) return '/default-avatar.png';
    
    if (url.startsWith('ipfs://')) {
      // Extract the CID (Content ID) from the IPFS URL
      const cid = url.replace('ipfs://', '');
      // Use a public IPFS gateway
      return `https://ipfs.io/ipfs/${cid}`;
    }
    
    return url;
  }
  
  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-brand">
          <Link to="/">
            <h1>ConnectUs</h1>
          </Link>
        </div>
        
        <div className="navbar-search" ref={searchRef}>
          <i className="search-icon">🔍</i>
          <input 
            type="text" 
            placeholder="Search ConnectUs" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          
          {showResults && searchResults.length > 0 && (
            <div className="search-results-dropdown">
              {searchResults.map(user => (
                <div 
                  key={user.username} 
                  className="search-result-item"
                  onClick={() => handleUserClick(user.username)}
                >
                  <img 
                    src={getIMGURL(user.profilePic) || '/default-avatar.png'} 
                    alt={user.username}
                    className="search-result-avatar"
                  />
                  <div className="search-result-info">
                    <span className="search-result-name">{user.displayName || user.username}</span>
                    <span className="search-result-username">@{user.username}</span>
                  </div>
                  
                  {user.username !== user?.username && (
                    <button 
                      className={`search-follow-btn ${user.isFollowing ? 'following' : ''}`}
                      onClick={(e) => handleFollowToggle(user.username, user.isFollowing, e)}
                    >
                      {user.isFollowing ? 'Following' : 'Follow'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {showResults && searchQuery.length >= 2 && searchResults.length === 0 && !loading && (
            <div className="search-results-dropdown">
              <div className="search-no-results">No users found</div>
            </div>
          )}
        </div>
        
        <div className="navbar-actions">
          {/* <Link to="/explore" className="nav-icon">
            <i>🔍</i>
          </Link>
          
          <Link to="/notifications" className="nav-icon">
            <i>🔔</i>
          </Link> */}
          
          <div className="profile-dropdown">
            <button 
              className="profile-button"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <img 
                src={getIMGURL(user?.profilePic) || '/default-avatar.png'} 
                alt={user?.username} 
                className="profile-avatar"
              />
            </button>
            
            {showDropdown && (
              <div className="dropdown-menu">
                <Link 
                  to={`/profile/${user?.username}`}
                  onClick={() => setShowDropdown(false)}
                >
                  Profile
                </Link>
                <button onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;